import { z } from 'zod';

/**
 * Geometry and persistence for panels the viewer can move.
 *
 * Floating chrome on a map is always in someone's way — the controls sit over
 * the storm you're trying to look at, or over the coastline you're using to
 * place it. Rather than guess one position that offends nobody, panels dock
 * somewhere sensible and can be dragged anywhere, and where they end up is
 * remembered.
 *
 * The maths lives here, away from React and the DOM, because "the panel ended
 * up half off the screen" is exactly the kind of bug that's miserable to
 * reproduce by hand and trivial to write a test for.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const pointSchema = z.object({ x: z.number(), y: z.number() });

/** Breathing room kept between a dragged panel and the edge of the map. */
export const PANEL_MARGIN = 12;

/**
 * Keeps a panel fully on screen.
 *
 * A stored position outlives the window it was chosen in: drag a panel to the
 * bottom-right of a wide monitor, reopen the app on a laptop, and the naive
 * restore puts it somewhere unreachable. Every restore and every drag frame
 * goes through this.
 *
 * A panel taller or wider than its container pins to the top-left margin —
 * clipping the bottom edge is better than clipping the header the viewer needs
 * in order to drag it back.
 */
export function clampToBounds(
  point: Point,
  panel: Size,
  bounds: Size,
  margin: number = PANEL_MARGIN,
): Point {
  const maxX = bounds.width - panel.width - margin;
  const maxY = bounds.height - panel.height - margin;

  return {
    x: Math.round(Math.min(Math.max(point.x, margin), Math.max(maxX, margin))),
    y: Math.round(Math.min(Math.max(point.y, margin), Math.max(maxY, margin))),
  };
}

/** Storage key for one panel's position. Versioned like every other stored shape. */
export function panelPositionKey(id: string): string {
  return `raydario.panel.${id}.v1`;
}
